import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, map, shareReplay } from "rxjs";
import { environment } from "../../../../environments/environment";
import { WeekHours } from "../../../domain/models/agreement.model";
import {
  ContractResourceModalData,
  ContractResourceModalDataResponse,
  CreateFakeResourcePayload,
  CreateFakeResourceResponse,
  PfGeo,
  PfGeoResponse,
} from "../../../domain/models/contract-resource.model";
import {
  FreezeCost,
  FreezeCostResponse,
} from "../../../domain/models/costs.model";
import {
  Discipline,
  DisciplineDTOResponse,
} from "../../../domain/models/discipline.model";
import {
  ProfessionalFigure,
  ProfessionalFigureResponse,
} from "../../../domain/models/figure.model";
import { Geo, GeosResponse } from "../../../domain/models/geo.model";
import { Resource } from "../../../domain/models/personnel.model";
import {
  Practice,
  PracticeResponse,
} from "../../../domain/models/practice.model";
import { HrRepository } from "../../../domain/repositories/hr.repository";

export interface WeekHoursResponse {
  totals: WeekHours[];
}

@Injectable()
export class HrRepositoryImpl extends HrRepository {
  private readonly endpoint: string = environment.apiUrl;

  private cachedFiguresResponse: Observable<ProfessionalFigure[]> | null = null;
  private cachedDisciplinesResponse: Observable<Discipline[]> | null = null;
  private cachedGeosResponse: Observable<Geo[]> | null = null;
  private cachedPratcicesResponse: Observable<Practice[]> | null = null;
  private cachedPfGeosResponse: Observable<PfGeo[]> | null = null;

  constructor(private http: HttpClient) {
    super();
  }

  override getAllProfessionalFigure(): Observable<ProfessionalFigure[]> {
    if (!this.cachedFiguresResponse) {
      this.cachedFiguresResponse = this.http
        .get<ProfessionalFigureResponse>(
          this.endpoint + "/hr/professional-figures",
        )
        .pipe(
          shareReplay(1),
          map((response) => response.prof),
        );
    }

    return this.cachedFiguresResponse;
  }

  override getAllGeos(): Observable<Geo[]> {
    if (!this.cachedGeosResponse) {
      this.cachedGeosResponse = this.http
        .get<GeosResponse>(this.endpoint + "/hr/geos")
        .pipe(
          shareReplay(1),
          map((response) => response.geos),
        );
    }
    return this.cachedGeosResponse;
  }

  override getAllPractices(): Observable<Practice[]> {
    if (!this.cachedPratcicesResponse) {
      this.cachedPratcicesResponse = this.http
        .get<PracticeResponse>(this.endpoint + "/hr/practices")
        .pipe(
          shareReplay(1),
          map((response) => response.prac),
        );
    }
    return this.cachedPratcicesResponse;
  }

  override getAllDisciplines(): Observable<Discipline[]> {
    if (!this.cachedDisciplinesResponse) {
      this.cachedDisciplinesResponse = this.http
        .get<DisciplineDTOResponse>(this.endpoint + "/hr/disciplines")
        .pipe(
          shareReplay(1),
          map((response) => response.disc),
        );
    }
    return this.cachedDisciplinesResponse;
  }

  override getAllActivePE(
    startDate: string,
    endDate: string,
  ): Observable<Resource[]> {
    return this.http
      .get<ActivePEResponse>(this.endpoint + "/hr/active-resources", {
        params: {
          startDate,
          endDate,
        },
      })
      .pipe(map((res) => res.resources));
  }

  override getWeekHoursByPracDisc(
    startDate: string,
    endDate: string,
    pracId: number,
    discId: number,
  ): Observable<WeekHours[]> {
    return this.http
      .get<WeekHoursResponse>(this.endpoint + "/hr/totals", {
        params: {
          startDate,
          endDate,
          pracId,
          discId,
        },
      })
      .pipe(map((response) => response.totals));
  }

  override getWeekHoursByPracDiscFPGeo(
    startDate: string,
    endDate: string,
    pracId: number,
    discId: number,
    professionalFigureId: string,
    geoId: string,
  ): Observable<WeekHours[]> {
    return this.http
      .get<WeekHoursResponse>(this.endpoint + "/hr/totals", {
        params: {
          startDate,
          endDate,
          pracId,
          discId,
          professionalFigureId,
          geoId,
        },
      })
      .pipe(map((response) => response.totals));
  }

  override getWeekHoursByPracDiscFPGeoRes(
    startDate: string,
    endDate: string,
    pracId: number,
    discId: number,
    professionalFigureId: string,
    geoId: string,
    resId: number,
  ): Observable<WeekHours[]> {
    return this.http
      .get<WeekHoursResponse>(this.endpoint + "/hr/totals", {
        params: {
          startDate,
          endDate,
          pracId,
          discId,
          professionalFigureId,
          geoId,
          resId,
        },
      })
      .pipe(map((response) => response.totals));
  }

  override retrieveContractResourceModalData(
    startDate: string,
    endDate: string,
  ): Observable<ContractResourceModalData> {
    return this.http
      .get<ContractResourceModalDataResponse>(
        this.endpoint + "/hr/active-resources-dropdown",
        {
          params: { startDate, endDate },
        },
      )
      .pipe(map((response) => response.dataDdl));
  }
  override createFakeResource(
    createFakeResourcePayload: CreateFakeResourcePayload,
  ): Observable<Resource> {
    return this.http
      .post<CreateFakeResourceResponse>(this.endpoint + "/hr/fake-resource", {
        createFakeResourceDto: createFakeResourcePayload,
      })
      .pipe(map((response) => response.resource));
  }
  override retrieveAllPfGeos(): Observable<PfGeo[]> {
    if (!this.cachedPfGeosResponse) {
      this.cachedPfGeosResponse = this.http
        .get<PfGeoResponse>(`${environment.apiUrl}/hr/professional-figure-geos`)
        .pipe(
          shareReplay(1),
          map((response) => response.pfGeos),
        );
    }
    return this.cachedPfGeosResponse;
  }

  override retrieveFreezeCosts(agreementId: number): Observable<FreezeCost[]> {
    return this.http
      .get<FreezeCostResponse>(
        `${environment.apiUrl}/sm/agreement/${agreementId}/cost-freeze`,
      )
      .pipe(map((response) => response.costFreez));
  }
}

export interface ActivePEResponse {
  resources: Resource[];
}
